import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import api from "../utils/api";

const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";

// Circular Progress Component for Trust Index
function CircularTrustIndex({ percentage = 0, title = "Actual Trust Index", colors }) {
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const segments = colors || [
    { color: "#FF6B9D", percent: 15 }, // Accuracy (pink/red)
    { color: "#4FC3F7", percent: 20 }, // Faithfulness (blue)
    { color: "#FF9800", percent: 10 }, // Explainability (orange)
    { color: "#E91E63", percent: 10 }, // Privacy (dark pink)
    { color: "#9C27B0", percent: 15 }, // Robustness (purple)
    { color: "#4CAF50", percent: 15 }, // Security (green)
    { color: "#00BCD4", percent: 15 }, // Ethics (cyan)
  ];

  let startAngle = 0;

  return (
    <Box sx={{ textAlign: "center", position: "relative" }}>
      <svg width="200" height="200" viewBox="0 0 200 200">
        {/* Background Circle */}
        <circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke="#f0f0f0"
          strokeWidth="20"
        />

        {/* Colored Segments */}
        {segments.map((seg, idx) => {
          const angle = (seg.percent / 100) * 360;
          const x1 = 100 + radius * Math.cos((startAngle - 90) * Math.PI / 180);
          const y1 = 100 + radius * Math.sin((startAngle - 90) * Math.PI / 180);
          const x2 = 100 + radius * Math.cos((startAngle + angle - 90) * Math.PI / 180);
          const y2 = 100 + radius * Math.sin((startAngle + angle - 90) * Math.PI / 180);
          const largeArc = angle > 180 ? 1 : 0;

          const path = `
            M 100 100
            L ${x1} ${y1}
            A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
            Z
          `;

          startAngle += angle;

          return (
            <path
              key={idx}
              d={path}
              fill={seg.color}
              opacity="0.9"
            />
          );
        })}

        {/* Inner White Circle */}
        <circle cx="100" cy="100" r="60" fill="white" />

        {/* Percentage Text */}
        <text
          x="100"
          y="95"
          textAnchor="middle"
          fontSize="32"
          fontWeight="700"
          fill="#1a1a1a"
        >
          {percentage}%
        </text>
      </svg>

      <Typography fontSize={14} fontWeight={600} mt={1}>
        {title}
      </Typography>
    </Box>
  );
}

// Main Component
export default function EvaluationDashboardPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (!projectId) {
          setError("Project ID is missing");
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        console.log("Fetching evaluation dashboard data for project:", projectId);
        const response = await api.get(`/api/evaluation-dashboard?project_id=${projectId}`);

        if (response.data.success) {
          setDashboardData(response.data.data);
          console.log("Dashboard data loaded:", response.data.data);
        } else {
          setError("Failed to load dashboard data");
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError(err.response?.data?.error || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [projectId]);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#fafafa" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  // Extract data from dashboardData
  const validationProgress = dashboardData?.validationProgress || [];
  const riskMapData = dashboardData?.riskMap || [];
  const useCaseMapData = dashboardData?.useCaseMap || [];
  const actualTrustIndex = dashboardData?.actualTrustIndex || 0;
  const targetTrustIndex = dashboardData?.targetTrustIndex || 89; // Default to 89 as requested
  const trustWeights = dashboardData?.trustWeights || {};

  // Calculate colors for circular trust index based on trust weights
  const attributeColors = {
    Accuracy: "#FF6B9D",
    Fairness: "#4FC3F7",
    Explainability: "#FF9800",
    Privacy: "#E91E63",
    Robustness: "#9C27B0",
    Security: "#4CAF50",
    Ethics: "#00BCD4"
  };

  const colors = Object.keys(trustWeights).map(attr => ({
    color: attributeColors[attr] || "#999",
    percent: trustWeights[attr] || 0
  }));

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          AI Test & Evaluation Dashboard
        </Typography>
        {/* <Typography variant="body2" color="text.secondary">
          Risk -> Metric -> Test -> Evidence
        </Typography> */}
        <Typography variant="body2" color="text.secondary">
          {"Risk -> Metric -> Test -> Evidence"}
        </Typography>

      </Box>

      {/* Validation Progress Section */}
      <Card sx={{ mb: 3, boxShadow: "none", border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Validation Progress
          </Typography>

          <Grid container spacing={3}>
            {/* Left: Table */}
            <Grid item xs={12} md={6}>
              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #f0f0f0" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f7f8fa" }}>
                      <TableCell><strong>Attributes</strong></TableCell>
                      <TableCell><strong>Weightage</strong></TableCell>
                      <TableCell><strong>Score</strong></TableCell>
                      <TableCell><strong>Trust Index</strong></TableCell>
                      {/* <TableCell><strong>Cumulative Trust Index</strong></TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationProgress.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ color: "#1976d2", fontWeight: 600 }}>{row.attribute}</TableCell>
                        <TableCell>{row.weightage}</TableCell>
                        <TableCell>{row.score}</TableCell>
                        <TableCell>{row.trustIndex}</TableCell>
                        {/* <TableCell>{row.cumulative}</TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Right: Circular Charts */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center", height: "100%" }}>
                <CircularTrustIndex percentage={actualTrustIndex} title="Actual Trust Index" colors={colors} />
                <CircularTrustIndex percentage={targetTrustIndex} title="Target Trust Index" colors={colors} />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Risk Map and Use Case Map Row */}
      <Grid container spacing={3} mb={3}>
        {/* Risk Map */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: "none", border: "1px solid #e0e0e0", borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Risk Map
                </Typography>
                <Button sx={{ color: "#1976d2", textTransform: "none", fontWeight: 600 }}>
                  View All
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #f0f0f0" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f7f8fa" }}>
                      <TableCell><strong>Risk Name</strong></TableCell>
                      <TableCell><strong>Metric</strong></TableCell>
                      <TableCell><strong>Threshold</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {riskMapData.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ color: "#1976d2", fontWeight: 600 }}>{row.riskName}</TableCell>
                        <TableCell>{row.metric}</TableCell>
                        <TableCell>{row.threshold}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Use Case Map */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: "none", border: "1px solid #e0e0e0", borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Use Case Map
                </Typography>
                <Button sx={{ color: "#1976d2", textTransform: "none", fontWeight: 600 }}>
                  View All
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #f0f0f0" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f7f8fa" }}>
                      <TableCell><strong>Use Case</strong></TableCell>
                      <TableCell><strong>Risk</strong></TableCell>
                      <TableCell><strong>Metric</strong></TableCell>
                      <TableCell><strong>Threshold</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {useCaseMapData.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ color: "#1976d2", fontWeight: 600 }}>{row.useCase}</TableCell>
                        <TableCell>{row.risk}</TableCell>
                        <TableCell>{row.metric}</TableCell>
                        <TableCell>{row.threshold}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Legend */}
      <Card sx={{ mb: 3, boxShadow: "none", border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
            {Object.keys(trustWeights).map((attr) => (
              <Box key={attr} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 16, height: 16, bgcolor: attributeColors[attr] || "#999", borderRadius: "50%" }} />
                <Typography fontSize={13}>{attr}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
